'use client'

import { useForm } from 'react-hook-form'
import { ErrorBox } from '@/General/Forms/ErrorBox'
import { useTurnstile } from '@/Turnstile'
import { Button } from '@/General/Button'
import { useSubmitContactFormMutation } from '@/generated/graphql'
import { useRouter } from 'next/navigation'
import { useState } from 'react'
import { EMAIL_REGEX } from '@/validation'

interface EnquiryFormData {
  name: string
  email: string
  details: string
}

export const EnquiryForm = () => {
  const router = useRouter()
  const [formData, setFormData] = useState<EnquiryFormData | null>(null)

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<EnquiryFormData>({
    defaultValues: { name: '', email: '', details: '' },
  })

  const { executeTurnstile } = useTurnstile()

  const { mutate: submitContactForm } = useSubmitContactFormMutation({
    onSuccess: (result) => {
      if (result.submitContactForm && formData) {
        const params = new URLSearchParams({
          email: formData.email,
          name: formData.name
        })
        router.push(`/book-a-magician/thank-you?${params.toString()}`)
      }
    },
    onError: () => {
      setError('root', {
        type: 'manual',
        message: 'Something went wrong. Please try again later or email us at magic@vanishingincmagic.com.',
      })
    },
  })

  const onSubmit = async (data: EnquiryFormData) => {
    try {
      const token = await executeTurnstile()
      const response = await fetch('/api/verifycaptcha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })

      if (!response.ok) {
        setError('root', {
          type: 'manual',
          message:
            'Unfortunately we were unable to verify that a human was submitting this form. Please try emailing us at magic@vanishingincmagic.com instead.',
        })
        return
      }

      setFormData(data)
      submitContactForm({
        name: data.name,
        email: data.email,
        reason: 'partnership', // Business development/partnership request
        question: `Magician booking enquiry: ${data.details}`,
        orderNumber: null
      })
    } catch {
      setError('root', {
        type: 'manual',
        message: 'Something went wrong. Please try again later or email us at magic@vanishingincmagic.com.',
      })
    }
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="needs-validation"
      noValidate
    >
      <ErrorBox
        errors={
          Object.values(errors)
            .map(error => error?.message)
            .filter(Boolean) as string[]
        }
      />
      <div className="mb-4">
        <input
          {...register('name', { required: 'Please enter your name' })}
          type="text"
          placeholder="Your name"
          className={`block w-full px-3 py-2 border ${
            errors.name ? 'border-vi-red' : 'border-vi-gray'
          } rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 text-lg text-black`}
        />
        {errors.name && (
          <p className="text-vi-red text-lg mt-1">{errors.name.message}</p>
        )}
      </div>
      <div className="mb-4">
        <input
          {...register('email', {
            required: 'Please enter your email address',
            pattern: {
              value: EMAIL_REGEX,
              message: 'Invalid email address',
            },
          })}
          type="email"
          placeholder="Your email address"
          className={`block w-full px-3 py-2 border ${
            errors.email ? 'border-vi-red' : 'border-vi-gray'
          } rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 text-lg text-black`}
        />
        {errors.email && (
          <p className="text-vi-red text-lg mt-1">{errors.email.message}</p>
        )}
      </div>
      <div className="mb-4">
        <textarea
          {...register('details', {
            required: 'Please provide details about your event',
          })}
          rows={8}
          placeholder="Details about your event"
          className={`block w-full px-3 py-2 border ${
            errors.details ? 'border-vi-red' : 'border-vi-gray'
          } rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 text-lg text-black`}
        />
        {errors.details && (
          <p className="text-vi-red text-lg mt-1">{errors.details.message}</p>
        )}
      </div>
      <Button type="submit" className="w-full">
        Submit Inquiry
      </Button>
          </form>
  )
}
